<template>
  <div :class="$style.container">
    <PriceTrendsByPlatform
      v-bind="$props"
      @onRangeChange="handleRangeChange"
      @onInfoIconClick="isModalVisible = true"
    >
      <template v-if="$slots.subtitle" #subtitle>
        <slot name="subtitle"></slot>
      </template>
      <template v-if="$slots.summary" #summary> <slot name="summary"></slot> </template>
    </PriceTrendsByPlatform>
    <HgModal
      id="price-trends-modal"
      v-model="isModalVisible"
      mode="dialog"
      @close="isModalVisible = false"
    >
      <template #title> {{ $t('localLanding.tooltips.title.priceTrends') }}</template>
      <div
        v-for="{ title, description } in modalContent"
        :key="title"
        :class="$style.tooltipContentWrapper"
      >
        <p :class="$style.tooltipContentHeading">{{ title }}</p>
        <p :class="$style.tooltipContentDescription" v-html="description" />
      </div>
    </HgModal>
  </div>
</template>

<script setup lang="ts">
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { PriceEvolutionResponse } from '@/models/queries/getPriceEvolution';
import { PriceTrendsRangeSummary, PropertyType } from '@/types';
import { HgModal } from '@smg-real-estate/domus';
import { computed, defineAsyncComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';

interface Props {
  priceTrendsData: PriceEvolutionResponse | null;
  propertyType: PropertyType;
  isLoading: boolean;
  hasModal?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  hasModal: false,
});

// Define emits
const emit = defineEmits<{
  onRangeChange: [value: PriceTrendsRangeSummary, isInitial: boolean];
}>();

const { t } = useI18n();

// Dynamic component based on platform
const PriceTrendsByPlatform = isPlatformIs24()
  ? defineAsyncComponent(() => import('@/components/common/PriceTrends/PriceTrendsIs24.vue'))
  : defineAsyncComponent(() => import('@/components/common/PriceTrends/PriceTrendsHg.vue'));

// Reactive data
const isModalVisible = ref(false);

// Computed properties
const modalContent = computed(() => [
  {
    title: t('localLanding.tooltips.dataUpdatePriceTrendsTitle'),
    description: t('localLanding.tooltips.dataUpdatePriceTrends'),
  },
  {
    title: t('localLanding.tooltips.marketVsOfferedTitle'),
    description: t('localLanding.tooltips.marketVsOffered'),
  },
  {
    title: t('localLanding.tooltips.medianPriceTitle'),
    description: t('localLanding.tooltips.medianPrice'),
  },
  {
    title: t('localLanding.tooltips.hedonicValuationTitle'),
    description: t('localLanding.tooltips.hedonicValuation'),
  },
]);

// Methods
const handleRangeChange = (value: PriceTrendsRangeSummary, isInitial: boolean): void => {
  emit('onRangeChange', value, isInitial);
};
</script>

<style module lang="scss">
@use '@/assets/variables' as *;

.container {
  position: relative;
}

.tooltipContentWrapper {
  margin-bottom: $space-250;
}

.tooltipContentDescription {
  @include vertical-margin;
  @include body-2;
}

.tooltipContentHeading {
  @include vertical-margin;
  @include heading-5;
}
</style>
